import { useMemo } from 'react';
import { rooms } from '@/data/rooms';
import { Room } from '@/types/room';
import { useProgression } from './useProgression';
import { safeGetJSON } from '@/lib/safeStorage';

interface VisitData {
  roomId: string;
  visitCount: number;
  lastVisit: number;
  totalTime: number;
}

const STORAGE_KEY = 'human-rooms-visit-data';

export const useRecommendations = () => {
  const { state, isRoomCompleted } = useProgression();

  const recommendations = useMemo((): Room[] => {
    const visitData = safeGetJSON<VisitData[]>(STORAGE_KEY, []);
    const completedRooms = state.completedRooms;
    
    // Get categories the user has engaged with most
    const categoryEngagement: Record<string, number> = {};
    completedRooms.forEach(roomId => {
      const room = rooms.find(r => r.id === roomId);
      if (room) {
        categoryEngagement[room.category] = (categoryEngagement[room.category] || 0) + 1;
      }
    });

    // Sort categories by engagement
    const topCategories = Object.entries(categoryEngagement)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3)
      .map(([cat]) => cat);

    // Get unvisited rooms
    const unvisitedRooms = rooms.filter(r => 
      !completedRooms.includes(r.id) && 
      !r.isPremium &&
      r.category !== 'secret'
    );

    // Score rooms based on category preference and recency
    const scoredRooms = unvisitedRooms.map(room => {
      let score = 0;
      
      // Category matching
      const categoryIndex = topCategories.indexOf(room.category);
      if (categoryIndex !== -1) {
        score += (3 - categoryIndex) * 10; // Top category gets 30, second 20, third 10
      }
      
      // Add some variety - slight bonus for categories not in top 3
      if (categoryIndex === -1 && topCategories.length >= 2) {
        score += 5; // Encourage exploration
      }
      
      // Boost daily and ritual rooms slightly
      if (room.category === 'daily' || room.category === 'ritual') {
        score += 8;
      }
      
      // Randomize a bit for variety
      score += Math.random() * 5;
      
      return { room, score };
    });

    // Sort by score and take top 6
    return scoredRooms
      .sort((a, b) => b.score - a.score)
      .slice(0, 6)
      .map(({ room }) => room);
  }, [state.completedRooms]);

  // Get "continue where you left off" rooms
  const continueRooms = useMemo((): Room[] => {
    const visitData = safeGetJSON<VisitData[]>(STORAGE_KEY, []);
    
    // Find rooms visited but not completed, sorted by last visit
    const unfinishedVisits = visitData
      .filter(v => !state.completedRooms.includes(v.roomId))
      .sort((a, b) => b.lastVisit - a.lastVisit)
      .slice(0, 3);

    return unfinishedVisits
      .map(v => rooms.find(r => r.id === v.roomId))
      .filter((r): r is Room => r !== undefined);
  }, [state.completedRooms]);

  return {
    recommendations,
    continueRooms,
    hasRecommendations: recommendations.length > 0,
    hasContinueRooms: continueRooms.length > 0,
  };
};
